/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { stickyUrlFlag } from '@/blackbolt/stickyUrlFlag'
import { CLAW_PROVIDER_ID } from '@/blackbolt/agent/agentProviderIds'
import { preferredAgentProvider } from '@/blackbolt/agent/agentProviderPref'

// Provider routing. `?agent=<id>` picks a registered provider transport for the
// session (a one-off dev override); absent, the persisted provider preference
// (the debug radio) selects bedrock; the `claw` default resolves to null
// so the built-in fallback runs.

/** URL param name that selects the chat-agent provider (e.g. `?agent=bedrock`). */
export const AGENT_PROVIDER_PARAM = 'agent'

/**
 * The selected provider id, or null when the built-in claw default applies.
 * Null (not `'claw'`) is the default sentinel the rest of the tier keys off:
 * `agentService.selectTransport` skips the registry lookup, and `agentWarmup`
 * skips warming — claw is statically wired and needs neither.
 */
export function selectedAgentProviderId(): string | null {
    // Stickied under 'agentProvider' so the choice survives the router
    // stripping the query before the turn-send path reads it.
    const fromUrl = stickyUrlFlag(AGENT_PROVIDER_PARAM, 'agentProvider')
    // Precedence: an explicit `?agent=` wins as a one-off override, else the
    // persisted single-identifier preference; `claw` → null (default).
    if (fromUrl) return fromUrl
    const preferred = preferredAgentProvider()
    return preferred === CLAW_PROVIDER_ID ? null : preferred
}
